import { Icon, tokens } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';

export interface ReadOnlyBannerProps {
  className?: string;
}

/**
 * ReadOnlyBanner — GVW-108.
 *
 * Persistent recipient banner for `#viewro=` links. The local editable-copy
 * action clears the read-only flag; server entitlement checks land later.
 */
export function ReadOnlyBanner({ className }: ReadOnlyBannerProps) {
  const readOnlyFromHook = useViewerStore((state) => state.readOnly);
  const readOnly = readOnlyFromHook || useViewerStore.getState().readOnly;
  const setReadOnly = useViewerStore.getState().setReadOnly;

  if (!readOnly) return null;

  return (
    <aside
      data-gvw-id="GVW-108"
      className={className}
      role="status"
      aria-live="polite"
      style={{
        width: '100%',
        position: 'relative',
        zIndex: tokens.zIndex.toast,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.space[3],
        padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
        borderBottom: tokens.border.thin,
        borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.info}`,
        background: tokens.color.surface[2],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: tokens.space[2] }}>
        <Icon name="lock" size={20} decorative />
        <span>
          <strong style={{ display: 'block', fontSize: tokens.font.size.sm }}>
            Read-only view
          </strong>
          <span style={{ display: 'block', color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>
            You are viewing a shared read-only link. Your edits will not be saved.
          </span>
        </span>
      </span>
      <button
        type="button"
        onClick={() => setReadOnly(false)}
        aria-label="Open this view as an editable copy"
        style={{
          minHeight: tokens.density.touchMin,
          border: tokens.border.thin,
          borderRadius: tokens.radius.input,
          background: tokens.color.surface[1],
          color: tokens.color.ink.primary,
          paddingInline: tokens.space[3],
          display: 'inline-flex',
          alignItems: 'center',
          gap: tokens.space[2],
          cursor: 'pointer',
        }}
      >
        <Icon name="edit" size={20} decorative />
        Open editable copy
      </button>
    </aside>
  );
}
